import { meetingResponseLabels, type Meeting, type MeetingResponse } from '@nexo/types';
import { Avatar, AvatarStack, Button, Modal, Spinner, cn, useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, CalendarPlus, Check, Copy, Phone, Video, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { cache } from '../../lib/cache';
import { useMeeting } from '../../lib/queries';
import { useMe } from '../auth/AuthProvider';
import { useStartCall } from '../calls/CallControls';
import { channelLabel } from '../channels/ChannelSidebar';
import { addToCalendar, formatMeetingTime, meetingLink, meetingState } from './meetingUtils';
import { can } from '../../lib/permissions';
import { errorMessage } from '../../lib/forms';

const responseTone: Record<MeetingResponse, string> = {
  yes: 'text-success',
  maybe: 'text-warning',
  no: 'text-danger',
  pending: 'text-subtle',
};

export function useMeetingActions(meetingId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  const onError = (err: unknown) =>
    toast({ title: t('No se pudo completar'), description: errorMessage(err), tone: 'error' });
  const respond = useMutation({
    mutationFn: (response: 'yes' | 'maybe' | 'no') => api.meetings.respond(meetingId, response),
    onSuccess: (updated) => cache.upsertMeeting(queryClient, updated),
    onError,
  });
  const cancel = useMutation({
    mutationFn: () => api.meetings.cancel(meetingId),
    onSuccess: (updated) => cache.upsertMeeting(queryClient, updated),
    onError,
  });
  return { respond, cancel };
}

/** RSVP buttons (yes / maybe / no) with the current answer highlighted. */
export function RsvpButtons({ meeting, size = 'sm' }: { meeting: Meeting; size?: 'sm' | 'md' }) {
  const me = useMe();
  const { respond } = useMeetingActions(meeting.id);
  const { t } = useTranslation();
  const mine = meeting.attendees.find((a) => a.id === me.id)?.response ?? 'pending';
  return (
    <div className="inline-flex rounded-lg bg-surface-2 p-0.5 ring-1 ring-inset ring-border">
      {(['yes', 'maybe', 'no'] as const).map((value) => (
        <button
          key={value}
          type="button"
          aria-pressed={mine === value}
          onClick={() => respond.mutate(value)}
          className={cn(
            'rounded-md px-2.5 font-medium transition-colors',
            size === 'sm' ? 'h-7 text-xs' : 'h-8 text-sm',
            mine === value ? 'bg-surface text-text shadow-panel' : 'text-muted hover:text-text',
          )}
        >
          {value === 'yes' ? t('Sí') : value === 'maybe' ? t('Quizá') : t('No')}
        </button>
      ))}
    </div>
  );
}

export function JoinMeetingButton({
  meeting,
  size = 'sm',
}: {
  meeting: Meeting;
  size?: 'sm' | 'md';
}) {
  const startCall = useStartCall();
  const { channels } = useTeam();
  const { t } = useTranslation();
  const voice = channels.find((c) => c.id === meeting.channelId)?.mode === 'voice';
  if (meetingState(meeting) !== 'live') return null;
  return (
    <Button
      size={size}
      icon={voice ? <Phone className="size-4" /> : <Video className="size-4" />}
      className="bg-green-600 hover:bg-green-700"
      onClick={() => void startCall(meeting.channelId, false)}
    >
      {t('Unirse')}
    </Button>
  );
}

/** Full meeting details (modal). */
export function MeetingDialog({
  meetingId,
  onClose,
}: {
  meetingId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal open={meetingId !== null} onClose={onClose} title={t('Reunión')} size="md">
      {meetingId && <MeetingDetails meetingId={meetingId} onClose={onClose} />}
    </Modal>
  );
}

function MeetingDetails({ meetingId, onClose }: { meetingId: string; onClose: () => void }) {
  const query = useMeeting(meetingId);
  const { channels, team } = useTeam();
  const me = useMe();
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();
  const meeting = query.data;
  const { cancel } = useMeetingActions(meetingId);
  if (query.isPending) {
    return (
      <div className="flex justify-center py-8 text-muted">
        <Spinner />
      </div>
    );
  }
  if (!meeting) return <p className="text-sm text-muted">{t('No se ha encontrado la reunión.')}</p>;
  const channel = channels.find((c) => c.id === meeting.channelId);
  const state = meetingState(meeting);
  const canManage = meeting.createdBy.id === me.id || can(team, 'manage_meetings');

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h3 className={cn('text-xl font-semibold', state === 'cancelled' && 'line-through')}>
          {meeting.title}
        </h3>
        <p className="mt-1 flex items-center gap-1.5 text-sm text-muted">
          <CalendarClock className="size-4" /> {formatMeetingTime(meeting)}
          {channel && (
            <span>
              · {channel.mode === 'voice' ? '🔊' : '#'} {channelLabel(channel)}
            </span>
          )}
        </p>
        {state === 'cancelled' && (
          <p className="mt-2 text-sm font-medium text-danger">
            {t('Esta reunión se ha cancelado.')}
          </p>
        )}
        {meeting.description && (
          <p className="mt-3 text-sm whitespace-pre-wrap">{meeting.description}</p>
        )}
      </div>

      {state !== 'cancelled' && state !== 'ended' && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted">{t('¿Asistirás?')}</span>
          <RsvpButtons meeting={meeting} size="md" />
          <span className="flex-1" />
          <JoinMeetingButton meeting={meeting} size="md" />
        </div>
      )}

      <section>
        <h4 className="mb-2 text-sm font-semibold">
          {t('Invitados · {{count}}', { count: meeting.attendees.length })}
        </h4>
        <ul className="flex flex-col gap-1.5">
          {meeting.attendees.map((a) => (
            <li key={a.id} className="flex items-center gap-2.5 text-sm">
              <Avatar id={a.id} name={a.displayName} src={a.avatarUrl} size="sm" />
              <span className="flex-1">
                {a.displayName}
                {a.id === meeting.createdBy.id && (
                  <span className="text-subtle"> · {t('organiza')}</span>
                )}
              </span>
              <span className={cn('text-xs font-medium', responseTone[a.response])}>
                {t(meetingResponseLabels[a.response])}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {meeting.guestUrl && (
        <section className="rounded-xl border border-border bg-surface-2 p-3">
          <p className="mb-2 text-sm font-medium">{t('Enlace para invitados externos')}</p>
          <div className="flex gap-2">
            <input
              readOnly
              value={meeting.guestUrl}
              onFocus={(e) => e.target.select()}
              className="h-9 min-w-0 flex-1 rounded-lg border border-border bg-surface px-3 font-mono text-xs"
            />
            <Button
              variant="secondary"
              icon={copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              onClick={() => {
                void navigator.clipboard.writeText(meeting.guestUrl ?? '');
                setCopied(true);
                toast({ title: t('Enlace copiado'), tone: 'success' });
              }}
            >
              {t('Copiar')}
            </Button>
          </div>
          <p className="mt-1.5 text-xs text-muted">
            {t('Funciona desde 15 minutos antes hasta una hora después del final.')}
          </p>
        </section>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
        {state !== 'cancelled' && (
          <Button
            variant="secondary"
            icon={<CalendarPlus className="size-4" />}
            onClick={() =>
              void addToCalendar(meeting, meetingLink(meeting)).catch((err: unknown) =>
                toast({
                  title: t('No se pudo exportar'),
                  description: errorMessage(err),
                  tone: 'error',
                }),
              )
            }
          >
            {t('Añadir al calendario')}
          </Button>
        )}
        <span className="flex-1" />
        {canManage && state !== 'cancelled' && state !== 'ended' && (
          <Button
            variant="ghost"
            icon={<X className="size-4" />}
            className="text-danger"
            loading={cancel.isPending}
            onClick={() => {
              if (
                window.confirm(
                  t('¿Cancelar «{{title}}»? Se avisará a los invitados.', { title: meeting.title }),
                )
              ) {
                cancel.mutate(undefined, { onSuccess: onClose });
              }
            }}
          >
            {t('Cancelar reunión')}
          </Button>
        )}
      </div>
    </div>
  );
}

/** Timeline card for the message that announces a meeting. */
export function MeetingCard({ meetingId }: { meetingId: string }) {
  const query = useMeeting(meetingId);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const meeting = query.data;
  if (!meeting) {
    return <div className="mt-1 h-20 max-w-md animate-pulse rounded-xl bg-surface-2" />;
  }
  const state = meetingState(meeting);
  return (
    <div
      className={cn(
        'mt-1 max-w-md rounded-xl border p-3',
        state === 'live' ? 'border-green-600/40 bg-green-600/10' : 'border-border bg-surface',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-start gap-3 text-left"
      >
        <span className="flex size-10 shrink-0 flex-col items-center justify-center rounded-lg bg-accent-soft text-accent">
          <CalendarClock className="size-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-medium tracking-wide text-subtle uppercase">
            {state === 'live'
              ? t('Reunión en curso')
              : state === 'cancelled'
                ? t('Reunión cancelada')
                : t('Reunión programada')}
          </span>
          <span
            className={cn('block truncate font-semibold', state === 'cancelled' && 'line-through')}
          >
            {meeting.title}
          </span>
          <span className="block text-xs text-muted">{formatMeetingTime(meeting)}</span>
        </span>
        <AvatarStack people={meeting.attendees} max={4} size="xs" />
      </button>
      {(state === 'upcoming' || state === 'live') && (
        <div className="mt-2.5 flex items-center gap-2">
          <RsvpButtons meeting={meeting} />
          <span className="flex-1" />
          <JoinMeetingButton meeting={meeting} />
        </div>
      )}
      <MeetingDialog meetingId={open ? meeting.id : null} onClose={() => setOpen(false)} />
    </div>
  );
}
